import { keepPreviousData, useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { tradingKeys } from '../tradingQueryKeys'
import { getSellQuote } from '../tradingEndpoints'
import { parseSellQuote } from '../tradingParser'

export interface SellQuoteRequest {
  tokens: number
  pricePerToken: number
}

const EMPTY_REQUEST: SellQuoteRequest = { tokens: 0, pricePerToken: 0 }

// Server-priced proceeds for a draft listing (POST, read-only). Rule
// violations come back in `errors`. The caller debounces `request`; null
// (an incomplete draft) pauses the query.
export function useSellQuoteQuery(
  propertyId: MaybeRefOrGetter<string>,
  request: MaybeRefOrGetter<SellQuoteRequest | null>,
) {
  return useQuery({
    queryKey: computed(() => {
      const draft = toValue(request) ?? EMPTY_REQUEST
      return tradingKeys.sellQuote(toValue(propertyId), draft.tokens, draft.pricePerToken)
    }),
    queryFn: () => {
      const draft = toValue(request) ?? EMPTY_REQUEST
      return getSellQuote(toValue(propertyId), draft.tokens, draft.pricePerToken).then(
        parseSellQuote,
      )
    },
    enabled: () => Boolean(toValue(propertyId)) && toValue(request) !== null,
    // The figures stay on screen while the next draft prices.
    placeholderData: keepPreviousData,
  })
}
